import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { Table } from 'primeng/table';
import { MessageService, ConfirmationService } from 'primeng/api';

import { ProductApiService } from 'src/app/services/product-api.service';
import { PriceVariable } from 'src/app/shared/price-variable';

/** `PriceVariable` plus the inline-row-edit bookkeeping fields this table
 *  needs — same `uid`/`cloneIndex` convention `edit-agent-bank-credentials-tab`
 *  and `edit-agent-team-members-tab` already established, not re-derived.
 *  `uid` mirrors `VARID` for an already-saved row; a not-yet-saved new row
 *  gets the `-2` sentinel until its real `VARID` is assigned at save time
 *  (`Variables` is not identity-keyed — see `routes/Variables.ts`'s own file
 *  header in webyto_api-express4-TS-20250331 — so there's no server-assigned
 *  id to wait for the way `AgentBankCredential.id` works; the new VARID is
 *  computed client-side, same MAX+1 convention `VariableSelectorComponent`
 *  already uses). */
type EditableVariable = PriceVariable & { uid: number; cloneIndex: number };

/**
 * Settings-section admin table for `[dbo].[Variables]` — the named
 * numeric variables (`VARCODE`/`VARVALUE`/`VARNOTE`) referenced by vendors'
 * `VPADDVAR`/`VPMULVAR` price-formula fields (see `edit-product-vendors`,
 * `VariableSelectorComponent`). That component already gained real Add/
 * Edit/Delete (B-27), but only as a small popup dialog reachable from
 * wherever the dropdown selector is embedded — this page is the
 * dedicated, standalone "list every variable in the system and manage it
 * in place" view, requested separately (owner, 2026-09-14) once the popup
 * version's Delete surfaced a real variable referenced by 59,017 vendor
 * price formulas: a case like that needs a page you can search and review,
 * not a dropdown.
 *
 * Inline row-editing, single-row-at-a-time (`editMode`), mirroring
 * `edit-agent-bank-credentials-tab.component.ts`'s shape rather than
 * re-deriving the pattern. Two differences from that precedent, both
 * because `Variables` isn't identity-keyed the way `AgentBankCredentials`
 * is: (1) a new row's real `VARID` is computed at save time, not read back
 * from the server response; (2) delete confirmation uses this app's
 * `ConfirmationService`/`ConfirmDialogModule` (newly wired into
 * `app.module.ts` by B-27's dialog work) rather than a plain `confirm()`,
 * since that's now the established pattern for a variable's delete flow
 * specifically — see `VariableSelectorComponent.onDeleteButton()`.
 */
@Component({
  selector: 'app-edit-price-variables-tab',
  templateUrl: './edit-price-variables-tab.component.html',
  styleUrl: './edit-price-variables-tab.component.css',
  encapsulation: ViewEncapsulation.None
})
export class EditPriceVariablesTabComponent implements OnInit, OnDestroy {

  variables: EditableVariable[] = [];
  variablesClone: EditableVariable[] = [];

  /** `true` while any row is being edited — same single-row-edit-at-a-time
   *  convention as `edit-agent-bank-credentials-tab.component.ts`'s own
   *  `editMode`. Gates every other row's pencil button plus "Добавить"/
   *  "Обновить" — reloading or adding mid-edit would replace `variables`
   *  out from under `variablesClone`'s cancel-restore and PrimeNG's own
   *  `editingRowKeys` state. */
  editMode: boolean = false;

  loading: boolean = false;

  /** Per-column filter input values, keyed by field name -- retained only for
   *  the `[class.hasvalue]` styling on each filter box (same `cf3` convention
   *  `edit-document-content.component.ts` already established); the actual
   *  filtering goes through PrimeNG's own `table.filter(value, field, mode)`. */
  colFilters: any = {};

  private loadQuery: Subscription = null;
  private saveQuery: Subscription = null;
  private deleteQuery: Subscription = null;

  @ViewChild('PV1') variablesTable: Table;

  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {
  }

  ngOnDestroy(): void {
    if (this.loadQuery) { this.loadQuery.unsubscribe(); }
    if (this.saveQuery) { this.saveQuery.unsubscribe(); }
    if (this.deleteQuery) { this.deleteQuery.unsubscribe(); }
  }

  ngOnInit(): void {
    this.loadVariables();
  }

  loadVariables(): void {
    this.loading = true;
    this.loadQuery =
      this.prodApi.getPriceVariables().subscribe({
        next: rows => {
          this.variables = rows.map(r => ({ ...r, uid: r.VARID, cloneIndex: 0 }));
          this.loading = false;
        },
        error: err => {
          console.error('*** EditPriceVariablesTabComponent loadVariables: Error: ' + err);
          this.loading = false;
          this.messageService.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить список переменных' });
        }
      });
  }

  reloadTable(): void {
    this.loadVariables();
  }

  /**
   * Owner-reported, 2026-09-14: a new row was pushed onto the *end* of
   * `variables`, so on any page but the last it appeared nowhere the user
   * could see -- worse once PR #243 added sorting/filtering, since neither
   * a blank new row's position under an active sort nor its visibility
   * under an active filter is predictable either. Fixed at the root: put
   * the row at the front of the array AND reset the table's own paging/
   * sort/filter state (`Table.reset()`, PrimeNG's own method for exactly
   * this) so it renders as the very first row of page 1 regardless of
   * where the user was. Clears `colFilters` too -- `reset()` only clears
   * PrimeNG's internal filter values, not this component's own input-box
   * model, and leaving stale text in the filter boxes while the table
   * itself shows unfiltered rows would be its own small, confusing bug.
   */
  addVariable(): void {
    const newRow: EditableVariable = { uid: -2, cloneIndex: 0, VARID: 0, VARCODE: '', VARVALUE: '', VARNOTE: '' };
    this.variables.unshift(newRow);

    this.variablesTable.reset();
    this.colFilters = {};

    this.onRowEditInit(newRow);
    this.variablesTable.initRowEdit(newRow);
  }

  onRowEditInit(row: EditableVariable): void {
    let newCloneIndex = 0;
    this.variablesClone.forEach(r => { newCloneIndex = Math.max(newCloneIndex, r.cloneIndex); });
    row.cloneIndex = ++newCloneIndex;
    this.variablesClone.push({ ...row });
    this.editMode = true;
  }

  onRowEditCancel(row: EditableVariable, index: number): void {
    if (row.uid === -2) {
      this.variables.splice(index, 1);
    } else {
      this.variables[index] = { ...this.variablesClone.find(r => r.cloneIndex === row.cloneIndex) };
      this.variables[index].cloneIndex = 0;
    }
    this.variablesClone.splice(this.variablesClone.findIndex(r => r.cloneIndex === row.cloneIndex), 1);
    this.editMode = false;
  }

  onRowEditSave(row: EditableVariable): void {
    // The sibling pSaveEditableRow directive on the same button already exits
    // the table's own row-edit state synchronously on this click, regardless
    // of validation or the save's outcome below -- editMode must follow that,
    // or a validation failure or a rejected save would leave every other
    // row's pencil (and "Добавить"/"Обновить") locked.
    this.editMode = false;

    if (!(row.VARCODE || '').trim()) {
      this.messageService.add({ severity: 'error', summary: 'Ошибка', detail: 'Код переменной обязателен' });
      return;
    }

    const isNew = row.uid === -2;
    const pv: PriceVariable = { VARID: row.VARID, VARCODE: row.VARCODE, VARVALUE: row.VARVALUE, VARNOTE: row.VARNOTE };

    if (isNew) {
      // Not identity-keyed -- see this file's header comment. Computed from
      // the currently-loaded list, excluding the unsaved row itself (still
      // VARID 0 at this point).
      pv.VARID = this.variables.reduce((max, v) => Math.max(max, v.VARID || 0), 0) + 1;
    }

    this.saveQuery =
      (isNew ? this.prodApi.postPriceVariableById(pv.VARID, pv) : this.prodApi.putPriceVariableById(pv.VARID, pv))
        .subscribe({
          next: () => {
            row.VARID = pv.VARID;
            row.uid = pv.VARID;
            this.variablesClone.splice(this.variablesClone.findIndex(r => r.cloneIndex === row.cloneIndex), 1);
            row.cloneIndex = 0;
            this.messageService.add({ severity: 'success', summary: 'Готово', detail: isNew ? 'Переменная добавлена' : 'Переменная обновлена' });
          },
          error: err => {
            console.error('*** EditPriceVariablesTabComponent onRowEditSave: Error: ' + err);
            this.messageService.add({ severity: 'error', summary: 'Не удалось сохранить', detail: err?.error?.error || err?.error?.message || err?.message || 'Unknown error' });
          }
        });
  }

  onDeleteVariable(row: EditableVariable, index: number): void {
    this.confirmationService.confirm({
      message: `Удалить переменную "${row.VARCODE}"?`,
      header: 'Подтверждение удаления',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Да',
      rejectLabel: 'Нет',
      accept: () => {
        this.deleteQuery =
          this.prodApi.deletePriceVariableById(row.VARID).subscribe({
            next: () => {
              this.variables.splice(index, 1);
              this.messageService.add({ severity: 'success', summary: 'Готово', detail: 'Переменная удалена' });
            },
            error: err => {
              console.error('*** EditPriceVariablesTabComponent onDeleteVariable: Error: ' + err);
              // 409 -- still referenced by one or more vendor price formulas.
              // The backend's own message already names the product count
              // and a sample (routes/Variables.ts) -- surface it verbatim
              // rather than a generic error, same reasoning
              // VariableSelectorComponent's delete flow already established.
              // Longer life than a normal toast: this message can be a full
              // sentence naming several products, worth the extra time to read.
              this.messageService.add({ severity: 'error', summary: 'Не удалось удалить', detail: err?.error?.error || err?.error?.message || err?.message || 'Unknown error', life: 10000 });
            }
          });
      }
    });
  }
}
